import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Logo from '../../assets/lumin-logo.png';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({
        type: 'success',
        text: 'Check your email for the password reset link!',
      });
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#016185] to-[#014a66] px-4">
      {/* Card */}
      <div className="w-full max-w-md">
        <div className="bg-[#016185] rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)]">
          {/* Header */}
          <div className="px-8 py-8 text-center">
            <img src={Logo} alt="Lumin Lending" className="h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
            <p className="text-white/70 text-sm mt-1">Enter your email to receive a reset link</p>
          </div>

          {/* Form */}
          <div className="bg-white px-8 py-8 rounded-b-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@luminlending.com"
                  required
                  autoFocus
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#016185] focus:border-[#016185] outline-none transition-all text-slate-900 placeholder:text-slate-400"
                />
              </div>

              {message && (
                <div
                  className={`p-4 rounded-xl text-sm font-medium ${
                    message.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {message.type === 'success' && (
                    <span className="mr-2">✓</span>
                  )}
                  {message.text}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3.5 px-4 bg-[#FC6217] text-white font-semibold rounded-xl hover:bg-[#e55a15] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-center text-sm text-slate-500">
                Remember your password?{' '}
                <Link to="/login" className="text-[#016185] hover:text-[#014a66] font-medium">
                  Back to login
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-white/50 mt-6">
          Lumin Lending &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
